import { Stack } from '@fluentui/react';
import React from 'react';

import {
  CardHolderAvailableTags,
  CardHolderAvailableTagsVariables,
} from './__generated__/CardHolderAvailableTags';
import {
  GetCardHolderTags,
  GetCardHolderTagsVariables,
} from './__generated__/GetCardHolderTags';
import {
  EntityTagCardHolderCreate,
  EntityTagCardHolderCreateVariables,
} from './__generated__/EntityTagCardHolderCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';
import { EntityTagInput } from 'common/types/globalTypes';
import { useStyles } from './index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';

import CARD_HOLDER_TAGS_LIST from './GetCardHolderTags.graphql';
import AVAILABLE_TAGS_LIST from './CardHolderAvailableTags.graphql';
import ADD_NEW_TAG from './EntityTagCardHolderCreate.graphql';
import DELETE_TAG from 'common/graphql/EntityTagDelete.graphql';

interface TagsSectionProps {
  cardAccountID?: string; // Renamed for clarity
}

export const TagsSection: React.FC<TagsSectionProps> = ({ cardAccountID }) => {
  const styles = useStyles();

  const extractAvailableTags = (data: CardHolderAvailableTags | undefined) => {
    return (
      data?.cardHolderAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? []
    );
  };

  const extractEntityTags = (data: GetCardHolderTags | undefined) => {
    return (
      data?.cardHolder?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? []
    );
  };

  const getAvailableTagsVars = (
    entityId: string
  ): CardHolderAvailableTagsVariables => ({
    id: entityId,
  });

  const getEntityTagsVars = (entityId: string): GetCardHolderTagsVariables => ({
    id: entityId,
  });

  const createTagsVars = (
    tags: EntityTagInput[]
  ): EntityTagCardHolderCreateVariables => ({
    input: {
      entityTag: tags,
    },
  });

  const deleteTagsVars = (
    id: string,
    rowTimestamp: string
  ): EntityTagDeleteVariables => ({
    entityTagDeleteInput: {
      entityDelete: [{ id, rowTimestamp }],
    },
  });

  if (!cardAccountID) return null;
  return (
    <EntityTagsSection<
      CardHolderAvailableTags,
      CardHolderAvailableTagsVariables,
      GetCardHolderTags,
      GetCardHolderTagsVariables,
      EntityTagCardHolderCreate,
      EntityTagCardHolderCreateVariables,
      EntityTagDelete,
      EntityTagDeleteVariables
    >
      entityId={cardAccountID}
      getAvailableTagsGql={AVAILABLE_TAGS_LIST}
      getEntityTagsGql={CARD_HOLDER_TAGS_LIST}
      addTagGql={ADD_NEW_TAG}
      deleteTagGql={DELETE_TAG}
      extractAvailableTags={extractAvailableTags}
      extractEntityTags={extractEntityTags}
      getAvailableTagsVars={getAvailableTagsVars}
      getEntityTagsVars={getEntityTagsVars}
      createTagsVars={createTagsVars}
      deleteTagsVars={deleteTagsVars}
    />
  );
};
